import { parseToDateTime } from '@calendly/datetime';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseMiniIcon } from '@calendly/ui/components/icons';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { Fragment, useMemo, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { DatePopup } from 'components/availability/form/date_specific_rules/date_popup';
import { useToggleState } from 'hooks/useToggleState';
import { formatTime, parseTime, shortUserDateRangeFormatWithNoYear, userDateFormat } from 'services/time_format';
import { Date, IntervalsContainer, ShowMoreButton } from './components';
import { MAX_RULES_TO_SHOW } from './constants';
import { RuleContainer, Wrapper, YearContainer } from './styles';
import { getRules, groupByYear } from '../../utils';

// Figma v4 date-specific rows: date label | status/times | trash — no year groups or gray cards.

const v4SectionCss = "v107r4ys";
const v4HoursGridCss = "v1p5nsdl";
const v4DateCellCss = "vb882rz";
const v4TimeCellCss = "v1j7uqhm";
const v4DeleteCellCss = "vju1n8l";
const v4EditButtonCss = "vpa8hw9";
const removeButtonContainerCss = "rhu025s";
const RemoveButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(removeButtonContainerCss, props.className)} />;
});
const formatV4DateRangeLabel = dateRange => {
  const fromDate = parseToDateTime(dateRange[0]);
  const toDate = parseToDateTime(dateRange[dateRange.length - 1]);
  if (dateRange.length === 1) {
    return userDateFormat(fromDate, 'american', 'date_small');
  }
  const [rangeStart, rangeEnd] = shortUserDateRangeFormatWithNoYear(fromDate, toDate);
  return `${rangeStart} – ${rangeEnd}`;
};
const formatV4IntervalRange = (interval, timeNotation) => {
  const from = formatTime(parseTime(interval.from, '24h'), timeNotation, timeNotation === '24h' ? undefined : 'h:mm a');
  const to = formatTime(parseTime(interval.to, '24h'), timeNotation, timeNotation === '24h' ? undefined : 'h:mm a');
  return `${from} – ${to}`;
};
const V4DateSpecificFields = ({
  dates,
  setDates,
  editingDates,
  setEditingDates,
  days,
  timezone,
  timeNotation,
  rulesGroupedByInterval,
  showMore,
  toggleShowMore,
  showShowMore
}) => {
  const rulesToShow = showMore ? rulesGroupedByInterval : rulesGroupedByInterval.slice(0, MAX_RULES_TO_SHOW);
  const removeDates = dateRange => {
    setDates(dates.filter(({
      date
    }) => !dateRange.includes(date)));
  };
  const handleEdit = dateRange => {
    setEditingDates(dateRange);
  };
  return <div className={v4SectionCss} data-component="rules-container" data-testid="v4-date-specific-fields">
      {rulesToShow.length > 0 && <div className={v4HoursGridCss}>
          {rulesToShow.map(({
        dates: dateRange,
        intervals
      }) => {
        const isUnavailable = intervals.length === 0;
        const editLabel = I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.edit_date_specific_hours_label', {
          dateRange
        });
        const removeLabel = I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.remove_date_specific_hours_label', {
          dateRange
        });
        return <Fragment key={dateRange.join('-')}>
                <BareButton aria-label={editLabel} className={classNames(v4EditButtonCss, v4DateCellCss)} onClick={() => handleEdit(dateRange)}>
                  <LabelText size="large">
                    {formatV4DateRangeLabel(dateRange)}
                  </LabelText>
                </BareButton>
                <BareButton aria-label={editLabel} className={classNames(v4EditButtonCss, v4TimeCellCss)} onClick={() => handleEdit(dateRange)}>
                  {isUnavailable ? <BodyText color="muted" size="medium">
                      {I18n.t('publisher.event_type_editor.sections.scheduling_settings.date_overrides.unavailable')}
                    </BodyText> : <BodyText size="medium">
                      {intervals.map(interval => formatV4IntervalRange(interval, timeNotation)).join(', ')}
                    </BodyText>}
                </BareButton>
                <div className={v4DeleteCellCss}>
                  <IconButton aria-label={removeLabel} size="small" onClick={() => removeDates(dateRange)} title={removeLabel}>
                    <UiIcon icon={IconTrash} size="small" />
                  </IconButton>
                </div>
              </Fragment>;
      })}
        </div>}
      {showShowMore && <ShowMoreButton totalRules={rulesGroupedByInterval.length} isExpanded={showMore} onClick={toggleShowMore} />}
      {editingDates && <DatePopup days={days} dates={dates} setDates={setDates} defaultDays={editingDates} onClose={() => setEditingDates(null)} timezone={timezone} />}
    </div>;
};
export const DateSpecificFields = ({
  days,
  dates,
  setDates,
  editingDates,
  setEditingDates,
  timezone,
  v4Styling = false
}) => {
  const {
    data: {
      time_notation: timeNotation
    }
  } = useGetUserQuery();
  const [showMore, toggleShowMore] = useToggleState(false);
  const rulesGroupedByInterval = useMemo(() => getRules(dates), [dates]);
  const formattedActiveDates = useMemo(() => {
    const rulesToShow = showMore ? rulesGroupedByInterval : rulesGroupedByInterval.slice(0, MAX_RULES_TO_SHOW);
    return groupByYear(rulesToShow).map(([year, items]) => [year, items.map(attrs => ({
      ...attrs,
      uid: nanoid(5)
    }))]);
  }, [rulesGroupedByInterval, showMore]);
  const showShowMore = rulesGroupedByInterval.length > MAX_RULES_TO_SHOW;
  const removeDates = dateRange => {
    setDates(dates.filter(({
      date
    }) => !dateRange.includes(date)));
  };
  if (v4Styling) {
    return <V4DateSpecificFields dates={dates} setDates={setDates} editingDates={editingDates} setEditingDates={setEditingDates} days={days} timezone={timezone} timeNotation={timeNotation} rulesGroupedByInterval={rulesGroupedByInterval} showMore={showMore} toggleShowMore={toggleShowMore} showShowMore={showShowMore} />;
  }
  return <Wrapper data-component="rules-container">
      {formattedActiveDates.map(([year, items]) => <YearContainer key={year} data-component={`${year}-rules`}>
          <LabelText size="medium">
            {year}
          </LabelText>
          {items.map(({
        dates: dateRange,
        intervals,
        uid
      }) => <RuleContainer key={uid} tabIndex={0} onClick={() => setEditingDates(dateRange)} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          setEditingDates(dateRange);
        }
      }} aria-label={I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.edit_date_specific_hours_label', {
        dateRange
      })}>
              <Date dateRange={dateRange} />
              <IntervalsContainer intervals={intervals} timeNotation={timeNotation} />
              <RemoveButtonContainer>
                <IconButton size="small" onClick={event => {
            event.stopPropagation();
            removeDates(dateRange);
          }} onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              removeDates(dateRange);
            }
          }} title={I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.remove_date_specific_hours_label', {
            dateRange
          })}>
                  <CloseMiniIcon />
                </IconButton>
              </RemoveButtonContainer>
            </RuleContainer>)}
        </YearContainer>)}
      {showShowMore && <ShowMoreButton totalRules={rulesGroupedByInterval.length} isExpanded={showMore} onClick={toggleShowMore} />}
      {editingDates && <DatePopup days={days} dates={dates} setDates={setDates} defaultDays={editingDates} onClose={() => setEditingDates(null)} timezone={timezone} />}
    </Wrapper>;
};

require("./date_specific_fields.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./date_specific_fields.js");